import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, ROLE_LABELS, isBranchUser } from "@/lib/auth";
import { BullMark } from "./BullMark";
import { Inbox, Upload, Users, Store, Percent, Plug, Mail, LineChart, LogOut, Camera, Building2, Menu, ChevronsLeft, ChevronsRight, X, Package, ScrollText, Sliders } from "lucide-react";

const items = [
  { to: "/documents", label: "Documents", icon: Inbox, roles: ["master_admin", "business_admin", "accounting_user", "branch_user"] },
  { to: "/upload", label: "Upload", icon: Upload, roles: ["master_admin", "business_admin", "accounting_user", "branch_user"] },
  { to: "/scan", label: "Scan (Camera)", icon: Camera, roles: ["master_admin", "business_admin", "accounting_user", "branch_user"] },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["master_admin", "business_admin"] },
  { to: "/vendors", label: "Vendors", icon: Store, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/items", label: "Items", icon: Package, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/vat-codes", label: "VAT Codes", icon: Percent, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/accounts", label: "Chart of Accounts", icon: ScrollText, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/business-defaults", label: "Business Defaults", icon: Sliders, roles: ["master_admin", "business_admin"] },
  { to: "/integrations", label: "Accounting", icon: Plug, roles: ["master_admin", "business_admin"] },
  { to: "/inbound", label: "Inbound Email", icon: Mail, roles: ["master_admin", "business_admin"] },
  { to: "/users", label: "Team", icon: Users, roles: ["master_admin", "business_admin"] },
  { to: "/stock-prices", label: "Stock Prices", icon: LineChart, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["master_admin", "business_admin", "accounting_user"] },
  { to: "/inbound-log", label: "Inbound Log", icon: Mail, roles: ["master_admin", "business_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("dochub_nav_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { localStorage.setItem("dochub_nav_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [nav]);

  const handleLogout = () => { logout(); nav("/login"); };
  const filtered = items.filter((i) => i.roles.includes(user?.role));

  // Branch user gets a stripped-down layout — just capture + view their uploads
  const isBranch = isBranchUser(user);

  const NavItem = ({ to, label, icon: Icon }) => (
    <NavLink
      to={to}
      data-testid={`nav-${to.replace("/", "")}`}
      title={collapsed ? label : undefined}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive ? "bg-espresso text-canvas" : "text-espresso-ink hover:bg-canvas-border/70"
        } ${collapsed ? "justify-center px-2" : ""}`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );

  const asideWidth = collapsed ? "w-16" : "w-64";
  const sidebar = (
    <aside className={`${asideWidth} shrink-0 border-r border-canvas-border bg-white flex flex-col transition-all duration-200 h-screen sticky top-0`} data-testid="sidebar">
      <div className="flex items-center gap-3 px-4 py-5 border-b border-canvas-border">
        <Link to={isBranch ? "/scan" : "/documents"} className="flex items-center gap-3 flex-1 min-w-0" data-testid="sidebar-logo">
          <BullMark className="w-9 h-9 shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display text-lg font-bold text-espresso tracking-tight">DocHub</div>
              <div className="text-[10px] uppercase tracking-widest text-espresso-ink/50 truncate">Brothers Coffee Group</div>
            </div>
          )}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex text-espresso-ink/60 hover:text-espresso p-1 rounded-sm hover:bg-canvas-border/50"
          data-testid="toggle-sidebar"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
        <button onClick={() => setMobileOpen(false)} className="md:hidden text-espresso-ink/60"><X className="w-5 h-5" /></button>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto scroll-canvas">
        {filtered.map((item) => <NavItem key={item.to} {...item} />)}
      </nav>
      <div className="px-3 py-4 border-t border-canvas-border">
        {!collapsed && (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gold/20 text-gold flex items-center justify-center font-display font-bold shrink-0">
              {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-espresso truncate">{user?.full_name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-widest text-espresso-ink/50">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title={collapsed ? "Sign out" : undefined}
          className={`w-full flex items-center gap-2 justify-center text-xs font-medium text-espresso-ink hover:text-espresso py-2 rounded-md border border-canvas-border hover:bg-canvas-border/50 transition-colors`}
        >
          <LogOut className="w-3.5 h-3.5" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-canvas">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">{sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-espresso/70 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed left-0 top-0 z-50 md:hidden">{sidebar}</div>
        </>
      )}

      <main className="flex-1 min-w-0 scroll-canvas h-screen overflow-y-auto">
        {/* Mobile header */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-canvas-border px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu" className="text-espresso-ink"><Menu className="w-6 h-6" /></button>
          <Link to={isBranch ? "/scan" : "/documents"} className="flex items-center gap-2">
            <BullMark className="w-8 h-8" />
            <span className="font-display font-bold text-espresso">DocHub</span>
          </Link>
          <div className="w-8 h-8 rounded-full bg-gold/20 text-gold flex items-center justify-center font-display font-bold text-sm">
            {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
          </div>
        </div>
        <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-6 md:py-10">{children}</div>
      </main>
    </div>
  );
}
